"use client";

import { ChakraProvider, EnvironmentProvider } from "@chakra-ui/react-v3";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import { ThemeProvider, type ThemeProviderProps } from "next-themes";
import { useEffect, useState } from "react";
import root from "react-shadow/emotion";
import { system } from "./system";

/**
 * Shadow DOM Provider for Chakra UI v3
 *
 * This is used to encapsulate the Chakra UI v3 components, preventing
 * them global styles from the Chakra v2 Provider from leaking into the Patient Page V2.
 * Once we migrate off of Chakra v2 completely, we can remove this provider.
 *
 * Based on official Chakra UI Shadow DOM guide:
 * https://www.chakra-ui.com/docs/get-started/environments/shadow-dom
 */
export function ShadowDOMProvider(props: ThemeProviderProps) {
  const [shadow, setShadow] = useState<HTMLElement | null>(null);
  const [cache, setCache] = useState<ReturnType<typeof createCache> | null>(null);

  useEffect(() => {
    if (!shadow?.shadowRoot || cache) return;
    const emotionCache = createCache({
      key: "root",
      container: shadow.shadowRoot,
    });
    setCache(emotionCache);
  }, [shadow, cache]);

  const RootDiv = root.div as React.ComponentType<
    React.HTMLAttributes<HTMLDivElement> & { ref?: React.Ref<HTMLDivElement> }
  >;

  return (
    <RootDiv ref={setShadow} id="shadow-root-host">
      {shadow && cache && (
        <EnvironmentProvider value={() => shadow.shadowRoot ?? document}>
          <CacheProvider value={cache}>
            <ChakraProvider value={system}>
              <ThemeProvider
                {...props}
                forcedTheme="light"
                enableSystem={false}
                defaultTheme="light"
              />
            </ChakraProvider>
          </CacheProvider>
        </EnvironmentProvider>
      )}
    </RootDiv>
  );
}
